// Enhanced CSRF protection utilities with cryptographically secure token generation
import { auth } from "./firebase";
import { sha256Sync, generateSecureRandomString, constantTimeCompare } from "../utils/crypto";

// Constants for token configuration
const TOKEN_EXPIRY_MS = 3600000; // 1 hour expiry

// Interface for token data structure with timestamp for expiration checking
interface CsrfTokenData {
  token: string;
  created: number;
  expires: number;
}

// Generate a cryptographically secure CSRF token
export const generateCsrfToken = (): string => {
  // Create a secure random string using the Web Crypto API
  const randomPart = generateSecureRandomString(32);
  const timestamp = Date.now();
  const userPart = auth.currentUser?.uid || 'nouser';
  
  // Create a unique token with user binding
  const rawToken = `${randomPart}:${timestamp}:${userPart}`;
  
  // Hash the token for additional security (using sync version for compatibility)
  return sha256Sync(rawToken);
};

// Store the CSRF token with metadata in sessionStorage
export const storeCsrfToken = (token: string): void => {
  const now = Date.now();
  const tokenData: CsrfTokenData = {
    token,
    created: now,
    expires: now + TOKEN_EXPIRY_MS
  };
  
  // Store as JSON string
  sessionStorage.setItem('csrf_token', JSON.stringify(tokenData));
};

// Get the stored CSRF token, checking expiration
export const getCsrfToken = (): string | null => {
  try {
    const storedData = sessionStorage.getItem('csrf_token');
    if (!storedData) return null;
    
    const tokenData: CsrfTokenData = JSON.parse(storedData);
    
    // Check if token has expired
    if (Date.now() > tokenData.expires) {
      // Token expired, clear it
      sessionStorage.removeItem('csrf_token');
      return null;
    }
    
    return tokenData.token;
  } catch (error) {
    console.error('Error retrieving CSRF token:', error);
    return null;
  }
};

// Generate and store a new CSRF token
export const refreshCsrfToken = (): string => {
  const token = generateCsrfToken();
  storeCsrfToken(token);
  return token;
};

// Validate that a token matches the stored token with timing-safe comparison
export const validateCsrfToken = (token: string): boolean => {
  const storedToken = getCsrfToken();
  if (!storedToken || !token) return false;
  
  // Use constant-time comparison to prevent timing attacks
  // This is a simple implementation - browsers don't have native timingSafeEqual
  if (token.length !== storedToken.length) return false;
  
  let result = 0;
  for (let i = 0; i < token.length; i++) {
    result |= token.charCodeAt(i) ^ storedToken.charCodeAt(i);
  }
  
  return result === 0;
};